import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";

import { IppyDippyCrudModel } from "./IppyDippyCrudModel";

export class GrowthPlan extends IppyDippyCrudModel {
  public static icon = "mdi-draw";
  protected static _typeLabel = "Growth Plan";
  protected static _typeLabelPlural = "Growth Plans";
  protected static asProperty = "growth_plans";
  public static api = {
    path: "growth-plans",
  };
  protected static routeBase = "growth-plans";
  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.EntityOwner]: UserPermission.Edit,
    },
    UserPermission.New
  );

  protected userIdField = [];

  protected static propertyDefinitions = [
    {
      type: "NumberProperty",
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: "DateTimeProperty",
      opts: {
        name: "created_at",
        displayRelative: true,
        label: "Entry Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: "DateTimeProperty",
      opts: {
        name: "updated_at",
        displayRelative: true,
        label: "Entry Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: "StringProperty",
      opts: {
        name: "label",
        label: "Growth Plan Label",
        rules: ["required"],
      },
    },
    {
      type: "StringProperty",
      opts: {
        name: "description",
        label: "Description",
        multiline: true,
      },
    },
    {
      type: "DateProperty",
      opts: {
        name: "submitted_date",
        label: "Submitted Date",
      },
    },
    {
      type: "DateProperty",
      opts: {
        name: "approved_date",
        label: "Approved Date",
      },
    },
    {
      type: "DateProperty",
      opts: {
        name: "start_date",
        label: "Start Date",
      },
    },
    {
      type: "DateProperty",
      opts: {
        name: "end_date",
        label: "End Date",
      },
    },
    {
      type: "RelationshipProperty",
      opts: {
        name: "user",
        label: "User",
        relatedModel: "User",
        sortable: true,
      },
    },
    {
      type: "RelationshipProperty",
      opts: {
        name: "group",
        label: "Group",
        relatedModel: "Group",
      },
    },
    {
      type: "RelationshipProperty",
      opts: {
        name: "approved_by",
        label: "Approved By",
        relatedModel: "User",
        sortable: true,
      },
    },
    {
      type: "RelationshipPropertyMany",
      opts: {
        name: "content_categories",
        label: "Content Categories",
        relatedModel: "ContentCategory",
        foreignProperty: "growth_plans",
      },
    },
    {
      type: "RelationshipPropertyMany",
      opts: {
        name: "credits",
        label: "Credits",
        relatedModel: "Credit",
        foreignProperty: "growth_plans",
        computed: true,
      },
    },
    {
      type: "JsonProperty",
      opts: {
        name: "cache_categories_progress",
        label: "Fidelity Scores",
        computed: true,
      },
    },
    {
      type: "NumberProperty",
      opts: {
        name: "cache_overall_progress",
        label: "Fidelity Score",
        computed: true,
        decoratorSuffix: "%",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: "NumberField", opts: { property: "id" } },
    {
      type: "StringField",
      opts: { property: "label", isPrimaryLabelField: 1 },
    },
    {
      type: "RelationshipField",
      opts: { property: "user" },
    },
    {
      type: "DateField",
      opts: { property: "start_date" },
    },
    {
      type: "DateField",
      opts: { property: "end_date" },
    },
    {
      type: "RelationshipFieldMany",
      opts: { property: "content_categories" },
    },
    {
      type: "WysiwygField",
      opts: { property: "description" },
    },
    {
      type: "RelationshipField",
      opts: {
        property: "group",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
            [UserRole.SuperUser]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      type: "RelationshipField",
      opts: {
        property: "approved_by",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
            [UserRole.SuperUser]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      type: "DateField",
      opts: { property: "submitted_date" },
    },
    {
      type: "DateField",
      opts: {
        property: "approved_date",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
            [UserRole.SuperUser]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      type: "DateTimeField",
      opts: { property: "created_at" },
    },
    { type: "DateTimeField", opts: { property: "updated_at" } },
    {
      type: "StringField",
      opts: {
        property: "cache_categories_progress",
        viewComponentName: "growth-plan-progress-bar",
        formComponentName: "growth-plan-progress-bar",
      },
    },
    {
      type: "NumberField",
      opts: {
        property: "cache_overall_progress",
      },
    },
    {
      type: "RelationshipFieldMany",
      opts: {
        property: "credits",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
        collectionLayoutOpts: {
          enableSearch: true,
          noRefresh: true,
          enableDynamicFilters: false,
          collectionActionComponents: [""],
          navigateForNew: true,
        },
      },
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "table",
      columnFields: [
        "label",
        "user",
        "group",
        "start_date",
        "end_date",
        "cache_overall_progress",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      useModal: true,
      dynamicFilterOptions: {
        filterFields: [
          "label",
          "user",
          "group",
          "content_categories",
          "start_date",
          "end_date",
          "approved_by",
          "submitted_date",
          "approved_date",
          "cache_overall_progress",
        ],
      },
      defaultSort: [
        {
          field: "end_date",
          order: "desc",
        },
      ],
    },
  ];

  protected static modelLayoutDefinitions = [
    {
      type: "TabbedModelLayout",
      opts: {
        id: "tabs",
        tabs: [
          {
            id: "overviewTab",
            label: "Overview",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "Crudform",
                fieldSets: [
                  {
                    id: "0",
                    fields: ["label", "user", "start_date", "end_date"],
                  },
                  {
                    id: "1",
                    fields: [
                      "cache_categories_progress",
                      "content_categories",
                      "description",
                    ],
                  },
                  {
                    id: "2",
                    fields: [
                      "group",
                      "approved_by",
                      "submitted_date",
                      "approved_date",
                      "created_at",
                      "updated_at",
                    ],
                  },
                ],
              },
            },
          },
          {
            id: "creditsTab",
            label: "Credits",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                fieldSets: [
                  {
                    id: "1",
                    fields: ["credits"],
                  },
                ],
              },
            },
          },
        ],
      },
    },
  ];
}
